import { ethers } from "ethers";
import { bytesToHexString, fromAsciiString, fromHexString, toAsciiString } from "./dwclient/dwcommonutils";
//let key: CryptoKey;
const SESSIONSTORAGE_USERKEY_SIGNATURE_KEY = "userKey";
// The mobile app derives the same key-signature during onboarding but keeps
// it in localStorage (see src/mobile/auth/mobileEncryption.ts). Falling back
// to it lets the shared readFile/writeFile helpers decrypt on mobile without
// prompting the wallet for another signature.
const MOBILE_USERKEY_SIGNATURE_KEY = "mobile_userKey_signature";
export const getSymmetricKey = async (provider) => {
    const signature = getKeySignature();
    let key;
    if (signature === null) {
        key = await createSymmetricKey(provider);
    }
    else {
        key = await createFromSignature(signature);
    }
    return key;
};
export const hasSavedSymmetricKey = () => {
    return getKeySignature() !== null;
};
export const getKeySignature = () => {
    return (sessionStorage.getItem(SESSIONSTORAGE_USERKEY_SIGNATURE_KEY) ||
        localStorage.getItem(MOBILE_USERKEY_SIGNATURE_KEY));
};
const createSymmetricKey = async (eip1193provider) => {
    //console.log("createSymmetricKey");
    // 1. Connect to the wallet
    const provider = new ethers.providers.Web3Provider(eip1193provider);
    await provider.send("eth_requestAccounts", []); // Request wallet connection
    const signer = provider.getSigner();
    const userAddress = await signer.getAddress();
    // 2. Define a user-specific static challenge
    const challenge = `This static text will be used to derive encryption key for ${userAddress}`;
    // 3. Sign the challenge
    const signature = await signer.signMessage(challenge);
    sessionStorage.setItem(SESSIONSTORAGE_USERKEY_SIGNATURE_KEY, signature);
    //console.log("before createFromSignature", signature);
    const key = await createFromSignature(signature);
    //console.log("after createFromSignature", key);
    return key;
};
export async function createFromSignature(signature) {
    // 4. Hash the signature to derive key material
    const hashBuffer = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(signature));
    const keyMaterial = new Uint8Array(hashBuffer);
    // 5. Derive a symmetric key
    const symmetricKey = await crypto.subtle.importKey("raw", keyMaterial, { name: "AES-GCM" }, true, [
        "encrypt",
        "decrypt",
    ]);
    return symmetricKey;
}
export async function encryptData(symmetricKey, plaintextBytes) {
    // Generate a 12-byte IV (Initialization Vector) for AES-GCM
    const iv = crypto.getRandomValues(new Uint8Array(12)); // 96 bits (12 bytes) is standard for AES-GCM
    //const iv = new Uint8Array([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]);
    // Encrypt the byte array (plaintextBytes)
    const encrypted = await crypto.subtle.encrypt({ name: "AES-GCM", iv: iv }, // AES-GCM with the IV
    symmetricKey, plaintextBytes);
    // Convert the ciphertext from ArrayBuffer to Uint8Array
    const ciphertext = new Uint8Array(encrypted);
    // Create a combined array: First 12 bytes are the IV, the rest is the ciphertext
    const combined = new Uint8Array(iv.length + ciphertext.length);
    combined.set(iv, 0); // Place the IV at the beginning
    combined.set(ciphertext, iv.length); // Place the ciphertext after the IV
    // Return the combined IV + ciphertext array (byte array)
    return combined;
}
export async function decryptData(symmetricKey, combinedData) {
    // Extract the IV (first 12 bytes)
    const iv = combinedData.slice(0, 12);
    // Extract the ciphertext (bytes after the first 12 bytes)
    const ciphertext = combinedData.slice(12);
    try {
        // Decrypt the ciphertext using the extracted IV and the symmetric key
        const decrypted = await crypto.subtle.decrypt({ name: "AES-GCM", iv: iv }, // AES-GCM with the extracted IV
        symmetricKey, ciphertext);
        // Return the decrypted byte array (Uint8Array)
        return new Uint8Array(decrypted);
    }
    catch (error) {
        console.log(error);
        return new Uint8Array();
    }
}
export const decryptToAsciiString = async (provider, encodedHexString) => {
    if (encodedHexString === "") {
        return "";
    }
    const key = await getSymmetricKey(provider);
    const input = fromHexString(encodedHexString);
    const decodedBytes = await decryptData(key, input);
    const decodedAsciiString = toAsciiString(decodedBytes);
    return decodedAsciiString;
};
export const encryptAsciiStringToHexString = async (provider, plainAsciiString) => {
    const encodedBytes = await encryptData(await getSymmetricKey(provider), fromAsciiString(plainAsciiString));
    const encodedHexString = bytesToHexString(encodedBytes);
    return encodedHexString;
};
export const encryptFile = async (provider, plainFile) => {
    const fileContent = await blobToUintArray(plainFile);
    const encodedBytes = await encryptData(await getSymmetricKey(provider), fileContent);
    return new File([encodedBytes], plainFile.name, { type: plainFile.type, lastModified: plainFile.lastModified });
};
export const decryptFile = async (provider, encryptedFile) => {
    const decryptedBlob = await decryptBlob(provider, encryptedFile);
    return new File([decryptedBlob], encryptedFile.name, {
        type: encryptedFile.type,
        lastModified: encryptedFile.lastModified,
    });
};
export const decryptBlob = async (provider, encryptedBlob) => {
    const fileContent = await blobToUintArray(encryptedBlob);
    const decodedBytes = await decryptData(await getSymmetricKey(provider), fileContent);
    return new Blob([decodedBytes], { type: encryptedBlob.type });
};
export const exportKeyAsHex = async (provider) => {
    return await exportCryptoKeyAsHex(await getSymmetricKey(provider));
};
export const exportCryptoKeyAsHex = async (cryptoKey) => {
    const rawKey = await crypto.subtle.exportKey("raw", cryptoKey);
    return bytesToHexString(new Uint8Array(rawKey));
};
const blobToUintArray = async (blob) => {
    const buffer = await new Response(blob).arrayBuffer();
    return new Uint8Array(buffer);
};
